import { formatDate } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import { Observable, observe, Scope } from 'wdc-cube'
import { ExportDocumentsScope } from 'src/components/exportdocuments/export_documents_scope'
import { GroupRow } from '../admin/group-manager/gm_scopes'
import { WaitingScope } from '../the-connection/tc_scopes'
import type {
    ApiKeyMetadata,
    CrmConnection,
    CrmDirection,
    ExportRequestSummary,
    FieldMappingEntry,
    GeneratedApiKey,
    OutboundAuthType,
    OutboundBodyAttribute,
    OutboundHeader,
    OverflowPolicy
} from './crm_integration_service'

export type HistoryQueryFilter = {
    startMonth: string
    endMonth?: string
    page: number
    limit: number
}

export type HistoryResponse<T> = HistoryQueryFilter & {
    totalRecords: number
    records: T[]
}
export type ExportHistoryRow = {
    seq?: number
    userId: number
    userName: string
    userFullName: string
    origem: string
    mesAno: Date
    dataHora: Date
    qtdRegistros: number
    groupId: number
    newLimitExport: number
    groupName: string
}

export type AggMesAnoHistoryRow = {
    mes_ano: string
    origem?: string
    qtd_registros: number
    group_id: number
    new_limit_export: number
    group_name: string
}

@Observable
export class HeaderScope extends Scope {}

@Observable
export class GroupProfileScope extends Scope {
    readonly header = new HeaderScope()

    readonly exportDocsScope = new ExportDocumentsScope()

    @observe() profileScope = new ProfileScope()
    @observe() historyScope = new HistoryScope()
    @observe() chartScope = new ChartHistoryScope()
    @observe() filterScope = {
        filters: {
            startMonth: formatDate(Date.now(), 'yyyy-MM-dd'),
            page: 0,
            limit: 10
        },
        startMonth: dayjs(Date.now())
    } as ProfileFilterScope

    @observe() canManageCrm = false

    readonly crmConnectionsScope = new CrmConnectionsScope()
    readonly crmApiKeysScope = new CrmApiKeysScope()
    readonly crmExportRequestsScope = new CrmExportRequestsScope()

    onGoBack = Scope.ASYNC_ACTION
}

@Observable
export class ProfileFilterScope extends Scope {
    @observe() filters = {} as HistoryQueryFilter
    @observe() startMonth: Dayjs = dayjs(Date.now())
    @observe() endMonth?: Dayjs

    onFind = Scope.ASYNC_ACTION_ONE<Partial<HistoryQueryFilter>>()
}

@Observable
export class ProfileScope extends Scope {
    @observe() waiting?: WaitingScope
    @observe() profile = {} as GroupRow
    @observe() savingBlockExport = false

    onToggleBlockExport = Scope.ASYNC_ACTION_BOOLEAN
}

@Observable
export class HistoryScope extends Scope {
    @observe() history_working = false
    @observe() historyVersion = 0
    @observe() history = {} as HistoryResponse<ExportHistoryRow>

    onViewDocuments = Scope.ASYNC_ACTION_NUMBER
}

@Observable
export class ChartHistoryScope extends Scope {
    @observe() history_chart_working = false
    @observe() historyChart = {} as HistoryResponse<AggMesAnoHistoryRow>
}

export type CrmConnectionForm = {
    crm_type: string
    name: string
    direction: CrmDirection
    endpoint_base: string
    path: string
    method: string
    authType: OutboundAuthType
    authHeaderName: string
    authToken: string
    headers: OutboundHeader[]
    bodyAttributes: OutboundBodyAttribute[]
    fieldMapping: FieldMappingEntry[]
}

export function novaCrmConnectionForm(): CrmConnectionForm {
    return {
        crm_type: '',
        name: '',
        direction: 'both',
        endpoint_base: '',
        path: '',
        method: 'POST',
        authType: 'header',
        authHeaderName: '',
        authToken: '',
        headers: [],
        bodyAttributes: [],
        fieldMapping: []
    }
}

@Observable
export class CrmConnectionsScope extends Scope {
    @observe() loading = false
    @observe() connections = [] as CrmConnection[]

    @observe() formOpen = false
    @observe() editingId?: number
    @observe() saving = false
    @observe() form = novaCrmConnectionForm()

    @observe() deleteTarget?: CrmConnection

    onNew = Scope.ASYNC_ACTION
    onEdit = Scope.ASYNC_ACTION_NUMBER
    onCancelForm = Scope.ASYNC_ACTION
    onFormChange = Scope.ASYNC_ACTION_ONE<Partial<CrmConnectionForm>>()
    onSubmit = Scope.ASYNC_ACTION
    onDelete = Scope.ASYNC_ACTION_NUMBER
    onConfirmDelete = Scope.ASYNC_ACTION
    onCancelDelete = Scope.ASYNC_ACTION
}

export type CrmApiKeyForm = {
    connectionId?: number
    name: string
    expiresAt: string
    overflowPolicy: OverflowPolicy
}

export function novaCrmApiKeyForm(): CrmApiKeyForm {
    return {
        connectionId: undefined,
        name: '',
        expiresAt: '',
        overflowPolicy: 'deny'
    }
}

@Observable
export class CrmApiKeysScope extends Scope {
    @observe() loading = false
    @observe() apiKeys = [] as ApiKeyMetadata[]
    @observe() connections = [] as CrmConnection[]

    @observe() formOpen = false
    @observe() saving = false
    @observe() form = novaCrmApiKeyForm()

    @observe() generatedKey?: GeneratedApiKey
    @observe() copied = false

    @observe() editing?: ApiKeyMetadata
    @observe() editName = ''
    @observe() editOverflowPolicy: OverflowPolicy = 'deny'

    @observe() revokeTarget?: ApiKeyMetadata

    onNew = Scope.ASYNC_ACTION
    onCancelForm = Scope.ASYNC_ACTION
    onFormChange = Scope.ASYNC_ACTION_ONE<Partial<CrmApiKeyForm>>()
    onGenerate = Scope.ASYNC_ACTION
    onCloseGeneratedKey = Scope.ASYNC_ACTION
    onCopyKey = Scope.ASYNC_ACTION
    onEdit = Scope.ASYNC_ACTION_NUMBER
    onEditChange = Scope.ASYNC_ACTION_ONE<{ name?: string; overflowPolicy?: OverflowPolicy }>()
    onSaveEdit = Scope.ASYNC_ACTION
    onCancelEdit = Scope.ASYNC_ACTION
    onRevoke = Scope.ASYNC_ACTION_NUMBER
    onConfirmRevoke = Scope.ASYNC_ACTION
    onCancelRevoke = Scope.ASYNC_ACTION
}

@Observable
export class CrmExportRequestsScope extends Scope {
    @observe() loading = false
    @observe() requests = [] as ExportRequestSummary[]

    @observe() authorizeTarget?: ExportRequestSummary
    @observe() authorizing = false

    @observe() reprocessTarget?: ExportRequestSummary
    @observe() reprocessing = false

    onReload = Scope.ASYNC_ACTION
    onAuthorize = Scope.ASYNC_ACTION_NUMBER
    onConfirmAuthorize = Scope.ASYNC_ACTION
    onCancelAuthorize = Scope.ASYNC_ACTION
    onReprocess = Scope.ASYNC_ACTION_NUMBER
    onConfirmReprocess = Scope.ASYNC_ACTION
    onCancelReprocess = Scope.ASYNC_ACTION
}
